import { CROPS, SEAFOOD, inventoryUsed, usableCropSeed } from './economy.js';
import { DISHES } from './cooking.js';

// The card, its primary action and plot cycling must choose the same bed.
export function selectFarmPlot(state, preferredId) {
  const plots = state.plots || [];
  return plots.find(plot => plot.id === preferredId)
    || plots.find(plot => plot.crop && plot.crop.growthMinutes >= CROPS[plot.crop.id]?.minutes)
    || plots.find(plot => plot.crop && plot.crop.growthMinutes < CROPS[plot.crop.id]?.minutes
      && state.gameMinutes >= plot.crop.wateredUntil)
    || plots.find(plot => !plot.crop)
    || plots[0] || null;
}

const bagFreeSlots = state => Math.max(0, (Number.isFinite(state.backpackCapacity) ? state.backpackCapacity : 20) - inventoryUsed(state));

// Modern saves hold the settled yield. Older crops may roll up, so reserve the
// maximum possible yield without drawing random numbers or changing the crop.
export function harvestReadiness(state, plot) {
  const spec = CROPS[plot?.crop?.id];
  const freeSlots = bagFreeSlots(state);
  if (!spec || plot.crop.growthMinutes < spec.minutes)
    return { canHarvest: false, quantity: 0, freeSlots, blockedReason: 'not-grown' };
  const quantity = Number.isInteger(plot.crop.expectedUnits) ? Math.max(0, Math.min(spec.yield, plot.crop.expectedUnits))
    : Math.max(0, Math.min(spec.yield, Math.ceil(spec.yield * (Number.isFinite(plot.yieldFactor) ? plot.yieldFactor : 1))));
  const blockedReason = freeSlots < quantity ? 'capacity' : state.stamina < 2 ? 'stamina' : null;
  return { canHarvest: !blockedReason, quantity, freeSlots, blockedReason };
}

export function plotGuidance(state, plot, preferredSeed = 'sweetPotato') {
  preferredSeed = Object.hasOwn(CROPS, preferredSeed) ? preferredSeed : 'sweetPotato';
  if (!plot?.crop) {
    const seed = usableCropSeed(state, preferredSeed);
    if (seed) return { action: '播种', operation: 'plant', page: 'farm', seedId: seed, status: '空田',
      detail: `${CROPS[seed].label}种子 ×${state.inventory[`${seed}Seed`]} · 点击播种`, ready: true };
    const storedSeed = usableCropSeed({ inventory: state.storage }, preferredSeed);
    if (storedSeed) {
      const seedId = `${storedSeed}Seed`, full = bagFreeSlots(state) < 1;
      return { action: full ? '整理背包' : '取回种子', operation: full ? 'manage-stock' : 'retrieve-seed',
        page: 'inventory', seedId: storedSeed, itemId: seedId, status: '空田',
        detail: full ? `仓库有${CROPS[storedSeed].label}种子 · 先腾出 1 格背包空间`
          : `仓库${CROPS[storedSeed].label}种子 ×${state.storage[seedId]} · 取回 1 包后可播种`, ready: false };
    }
    return { action: '购买种子', operation: 'buy-seed', page: 'shop', seedId: preferredSeed, itemId: `${preferredSeed}Seed`,
      status: '空田', detail: '背包缺农田种子 · 在码头购入', ready: false };
  }
  const spec = CROPS[plot.crop.id];
  const remaining = Math.max(0, Math.ceil(spec.minutes - plot.crop.growthMinutes));
  if (!remaining) {
    const harvest = harvestReadiness(state, plot);
    if (harvest.blockedReason === 'capacity') return { action: '整理背包', operation: 'manage-stock', page: 'inventory',
      status: `${spec.label} · 已成熟`, detail: `收获需 ${harvest.quantity} 格，背包剩余 ${harvest.freeSlots} 格 · 先出售或存仓`,
      ready: false, ...harvest };
    if (harvest.blockedReason === 'stamina') return { action: '稍歇恢复', operation: 'recover', page: 'farm',
      status: `${spec.label} · 已成熟`, detail: '收获需 2 体力 · 稍歇几秒自然恢复，作物会留在田里',
      ready: false, ...harvest };
    return { action: '收获', operation: 'harvest', page: 'farm', status: `${spec.label} · 已成熟`,
      detail: harvest.quantity ? `点击收获成熟的${spec.label} ×${harvest.quantity} · 作物装入背包`
        : '作物已受损 · 点击清理田块，不占背包空间', ready: true, ...harvest };
  }
  const dry = state.gameMinutes >= plot.crop.wateredUntil;
  const paused = Math.max(0, Math.ceil((plot.pauseUntil || 0) - state.gameMinutes));
  const status = `${spec.label} · ${Math.floor(plot.crop.growthMinutes / spec.minutes * 100)}%`;
  if (dry && !(state.freshwater?.canteen > 0)) return { action: '装满水壶', operation: 'refill-water', page: 'water', status,
    detail: `缺水暂停 · 先到淡水收集场装水，再给${spec.label}浇水`, ready: false };
  if (dry && state.stamina < 1) return { action: '稍歇恢复', operation: 'recover', page: 'farm', status,
    detail: '浇水需 1 体力 · 稍歇几秒自然恢复，再回来浇水', ready: false };
  return { action: dry ? '浇水' : '查看生长', operation: dry ? 'water' : 'inspect', page: 'farm', status,
    detail: dry ? `缺水暂停 · 浇水需 1 L 水 + 1 体力 · 还需生长 ${remaining} 游戏分钟`
      : paused ? `水分充足 · 受灾停长还剩 ${paused} 分钟 · 再生长 ${remaining} 分钟`
        : `水分充足 · 还需生长 ${remaining} 游戏分钟`, ready: dry };
}

export function nextPlayerAction(state) {
  if (state.stamina < 20) return { page: 'inventory', text: '体力偏低 · 稍歇几秒自然恢复，也可食用补给' };
  const ripe = state.plots?.find(p => p.crop && p.crop.growthMinutes >= CROPS[p.crop.id]?.minutes);
  if (ripe) {
    const hint = plotGuidance(state, ripe);
    return { page: hint.page, plotId: ripe.id, text: hint.ready ? '收获成熟作物 · 装入背包，可烹饪或出售' : hint.detail };
  }
  const dry = state.plots?.find(p => p.crop && p.crop.growthMinutes < CROPS[p.crop.id]?.minutes && state.gameMinutes >= p.crop.wateredUntil);
  if (dry) return { page: state.freshwater?.canteen > 0 ? 'farm' : 'water', plotId: dry.id, text: state.freshwater?.canteen > 0 ? '给缺水作物浇水 · 让作物继续生长' : '先装满水壶 · 缺水作物正在暂停生长' };
  const empty = state.plots?.find(p => !p.crop);
  if (empty) {
    const hint = plotGuidance(state, empty);
    if (hint.ready) return { page: 'farm', text: '在空田播种 · 使用背包里的农田种子' };
    if (hint.operation === 'retrieve-seed' || hint.operation === 'manage-stock')
      return { page: hint.page, plotId: empty.id, itemId: hint.itemId, text: hint.detail };
  }
  return { page: 'voyage', text: '探索群岛 · 在海图查看船体和随身补给' };
}

export function voyageSupplies(state) {
  const food = Object.entries(state.inventory || {}).reduce((sum, [id, count]) => sum + ((CROPS[id] || SEAFOOD[id] || DISHES[id]) ? count : 0), 0);
  return { food, summary: `船体 ${Math.round(state.boatDurability)}/100 · 水壶 ${state.freshwater?.canteen || 0} L · 食物 ${food} 份`,
    advice: state.boatDurability <= 0 || state.boatTier === 'wreck' ? '船已损坏 · 先到维修仓储修船'
      : `驾船可恢复体力 · 食物和饮水是可选补给 · 航行按正常时间进行` };
}
